import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { appTileLabel } from './coverageHelpers';
import { RoleKeyCreateForm } from './RoleKeyCreateForm';
import type { RoleKeyCreateFormVM } from './useRoleKeyCreateForm';
import shared from './coverageShared.module.css';
import styles from './EmptyStates.module.css';

/**
 * EAA-1817: frame 60 ("Trạng thái rỗng & đang tải") — the two dead-end states
 * the editor card can land in, plus a plain "chưa chọn" fallback. Each is a
 * standalone `.hz-card`-shaped panel, matching the design's stacked cards
 * rather than one combined message.
 */

export function AppNotApprovedEmptyState({
    appCode,
    appName,
    canManageApps,
    onOpenApp,
}: {
    appCode: string;
    appName: string;
    canManageApps: boolean;
    onOpenApp: () => void;
}) {
    const { t } = useTranslation();
    return (
        <section
            className={`hz-card ${styles.emptyCard}`}
            aria-label={t('console.roleCoverage.appNotApproved.ariaLabel', { appName })}
        >
            <span className={shared.appTile} aria-hidden="true">
                {appTileLabel(appCode)}
            </span>
            <h3 className={styles.emptyTitle}>
                {t('console.roleCoverage.appNotApproved.title', { appName })}
            </h3>
            <p className={shared.cardNote}>
                {t('console.roleCoverage.appNotApproved.description')}
            </p>
            {canManageApps && (
                // EAA-1804: was `<Button className="portal-btn portal-btn-primary">` — a
                // leftover EAA-1817 partial migration that kept the legacy classes instead
                // of the antd variant prop. Corrected to `type="primary"`; see
                // AccountDetailModal.tsx for the mapping rationale.
                <Button type="primary" onClick={onOpenApp}>
                    {t('console.roleCoverage.appNotApproved.openAppButton')}
                </Button>
            )}
        </section>
    );
}

export function NoRoleKeysEmptyState({
    canManage,
    form,
}: {
    canManage: boolean;
    form: RoleKeyCreateFormVM;
}) {
    const { t } = useTranslation();
    return (
        <section
            className={`hz-card ${styles.emptyCard}`}
            aria-label={t('console.roleCoverage.noRoleKeys.ariaLabel')}
        >
            <h3 className={styles.emptyTitle}>{t('console.roleCoverage.noRoleKeys.title')}</h3>
            <p className={shared.cardNote}>{t('console.roleCoverage.noRoleKeys.description')}</p>

            {canManage && !form.open && (
                // EAA-1804: was `<Button className="portal-btn portal-btn-primary">` —
                // corrected to `type="primary"` (see AccountDetailModal.tsx).
                <Button type="primary" onClick={() => form.setOpen(true)}>
                    {t('console.roleCoverage.noRoleKeys.createButton')}
                </Button>
            )}

            {canManage && form.open && <RoleKeyCreateForm form={form} />}
        </section>
    );
}

export function NoAppSelectedEmptyState() {
    const { t } = useTranslation();
    return (
        <section
            className={`hz-card ${styles.emptyCard}`}
            aria-label={t('console.roleCoverage.noAppSelected.ariaLabel')}
        >
            <p className="portal-admin-empty">
                {t('console.roleCoverage.noAppSelected.description')}
            </p>
        </section>
    );
}
